import moment from 'moment';

import { evaluateAllConditions, evaluateCondition } from '../config/evaluateCondition';
import { isConfigEnabled } from '../config/isConfigEnabled';

/**
 * Returns negotiation status dropdown options from behavior config.
 */
export const getNegotiationStatusOptions = (behaviorConfig) => {
    const options = behaviorConfig?.negotiationStatus?.options || [];

    return options.map((status) => ({
        label: status,
        value: status,
    }));
};

/**
 * Returns true when a date field holds a valid non-empty value.
 */
const hasApprovalDate = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    return moment.parseZone(value).isValid();
};

/**
 * Derives negotiationStatus from configured priority rules.
 */
export const deriveNegotiationStatus = (row = {}, behaviorConfig) => {
    const autoUpdate = behaviorConfig?.negotiationStatus?.autoUpdate;
    const context = {
        topTierField: behaviorConfig?.negotiationStatus?.topTierField || 'topTierRequired',
        tierApprovalDateFields: autoUpdate?.tierApprovalDateFields || {},
    };

    const enabledRules = (autoUpdate?.rules || []).filter((rule) => isConfigEnabled(rule));

    for (const rule of enabledRules) {
        if (evaluateCondition(row, rule.condition, context)) {
            return rule.set;
        }
    }

    return 'Deal Review';
};

/**
 * Returns true when saving this field can affect negotiationStatus.
 */
const isNegotiationStatusTriggerField = (fieldKey, row, behaviorConfig) => {
    const autoUpdate = behaviorConfig?.negotiationStatus?.autoUpdate;
    const topTierField = behaviorConfig?.negotiationStatus?.topTierField || 'topTierRequired';
    const approvalDateField = behaviorConfig?.negotiationStatus?.approvalDateField || 'approvalDate';

    if ((autoUpdate?.triggerFields?.always || []).includes(fieldKey)) {
        return true;
    }

    if (!autoUpdate?.triggerFields?.includeRelevantTierApprovalDate) {
        return false;
    }

    const topTierVal = row[topTierField];
    const tierMatch = topTierVal == null ? null : String(topTierVal).match(/tier\s*(\d+)/i);
    const tierNumber = tierMatch ? parseInt(tierMatch[1], 10) : null;
    const approvalDateKey = tierNumber != null
        ? autoUpdate.tierApprovalDateFields?.[tierNumber]
        : null;

    return approvalDateKey != null && fieldKey === approvalDateKey;
};

/**
 * Returns true when automatic negotiationStatus updates should be skipped.
 */
const shouldSkipNegotiationStatusAutoUpdate = (row, fieldKey, behaviorConfig) => {
    const statusField = behaviorConfig?.negotiationStatus?.field || 'negotiationStatus';
    const autoUpdate = behaviorConfig?.negotiationStatus?.autoUpdate;

    if (fieldKey === autoUpdate?.skipWhenSavingField) {
        return true;
    }

    const currentStatus = row?.[statusField];
    return (autoUpdate?.skipWhenCurrentValueIn || []).includes(currentStatus);
};

/**
 * Collects dependency field keys for table API requests.
 */
export const getNegotiationStatusDependencyFields = (behaviorConfig) => {
    if (!isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        return [];
    }

    const statusField = behaviorConfig.negotiationStatus.field || 'negotiationStatus';
    const approvalDateField = behaviorConfig.negotiationStatus.approvalDateField || 'approvalDate';
    const topTierField = behaviorConfig.negotiationStatus.topTierField || 'topTierRequired';
    const tierApprovalDateFields = Object.values(
        behaviorConfig.negotiationStatus.autoUpdate?.tierApprovalDateFields || {},
    );

    return [statusField, approvalDateField, topTierField, ...tierApprovalDateFields];
};

/**
 * Returns true when a required field value is present for manual status validation.
 */
const hasManualValidationFieldValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return false;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return false;
        }

        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed) || /^\d{2}\/\d{2}\/\d{4}/.test(trimmed)) {
            return moment.parseZone(trimmed).isValid();
        }

        return true;
    }

    return hasApprovalDate(value);
};

/**
 * Normalizes status values for case-insensitive comparison.
 */
const normalizeStatusValue = (value) => (
    value == null ? '' : String(value).trim().toLowerCase()
);

/**
 * Returns an error message when manually setting negotiationStatus violates manualValidation rules.
 * Example: cannot set "Negotiation Completed" when approvalDate is empty.
 */
export const getManualNegotiationStatusValidationError = (
    fieldKey,
    nextStatusValue,
    row = {},
    behaviorConfig,
) => {
    if (!isConfigEnabled(behaviorConfig?.negotiationStatus)) {
        return null;
    }

    const statusField = behaviorConfig.negotiationStatus.field || 'negotiationStatus';
    if (fieldKey !== statusField) {
        return null;
    }

    const manualValidation = behaviorConfig.negotiationStatus.manualValidation;
    if (!isConfigEnabled(manualValidation)) {
        return null;
    }

    const rules = (manualValidation.rules || []).filter((rule) => isConfigEnabled(rule));
    if (!rules.length) {
        return null;
    }

    const normalizedNextStatus = normalizeStatusValue(nextStatusValue);
    if (!normalizedNextStatus) {
        return null;
    }

    const mergedRow = { ...row, [statusField]: nextStatusValue };

    for (let index = 0; index < rules.length; index += 1) {
        const rule = rules[index];
        if (normalizeStatusValue(rule.status) !== normalizedNextStatus) {
            continue;
        }

        const requireOf = Array.isArray(rule.requireOf) ? rule.requireOf : [];
        const requireAnyOf = Array.isArray(rule.requireAnyOf) ? rule.requireAnyOf : [];

        if (requireOf.length > 0) {
            const hasAllRequired = requireOf.every((requiredField) => (
                hasManualValidationFieldValue(mergedRow[requiredField])
            ));

            if (!hasAllRequired) {
                return rule.message
                    || `Please complete required fields before setting status to ${rule.status}.`;
            }
        }

        if (requireAnyOf.length > 0) {
            const hasAnyRequired = requireAnyOf.some((requiredField) => (
                hasManualValidationFieldValue(mergedRow[requiredField])
            ));

            if (!hasAnyRequired) {
                return rule.message
                    || `Please complete required fields before setting status to ${rule.status}.`;
            }
        }
    }

    return null;
};

/**
 * Returns negotiationStatus payload when a trigger field is saved or cleared.
 */
export const getNegotiationStatusAutoUpdatePayload = (
    fieldKey,
    value,
    row = {},
    behaviorConfig,
) => {
    if (
        !isConfigEnabled(behaviorConfig)
        || !isConfigEnabled(behaviorConfig.negotiationStatus)
        || !isConfigEnabled(behaviorConfig.negotiationStatus.autoUpdate)
    ) {
        return {};
    }

    if (!row || shouldSkipNegotiationStatusAutoUpdate(row, fieldKey, behaviorConfig)) {
        return {};
    }

    const statusField = behaviorConfig.negotiationStatus.field || 'negotiationStatus';
    const topTierField = behaviorConfig.negotiationStatus.topTierField || 'topTierRequired';
    const effectiveTopTier = fieldKey === topTierField ? value : row[topTierField];

    if (!isNegotiationStatusTriggerField(fieldKey, { ...row, [topTierField]: effectiveTopTier }, behaviorConfig)) {
        return {};
    }

    const mergedRow = { ...row, [fieldKey]: value };
    const derivedStatus = deriveNegotiationStatus(mergedRow, behaviorConfig);
    const currentStatus = row[statusField];

    if (derivedStatus === currentStatus) {
        return {};
    }

    return { [statusField]: derivedStatus };
};
